import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

// ─── Toast (replaces window.alert) ─────────────────────────────────────────────

type ToastType = 'success' | 'error' | 'info';
interface ToastItem { id: number; message: string; type: ToastType; }
interface ToastContextValue { showToast: (message: string, type?: ToastType) => void; }

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue['showToast'] {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within NotificationsProvider');
  return ctx.showToast;
}

// ─── Confirm dialog (replaces window.confirm) ──────────────────────────────────

interface ConfirmOpts { confirmLabel?: string; cancelLabel?: string; danger?: boolean; }
interface ConfirmState extends ConfirmOpts { message: string; resolve: (v: boolean) => void; }
interface ConfirmContextValue { confirm: (message: string, opts?: ConfirmOpts) => Promise<boolean>; }

const ConfirmContext = createContext<ConfirmContextValue | null>(null);

export function useConfirm(): ConfirmContextValue['confirm'] {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within NotificationsProvider');
  return ctx.confirm;
}

// ─── Combined provider ──────────────────────────────────────────────────────────

let toastId = 0;

export function NotificationsProvider({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [confirmState, setConfirmState] = useState<ConfirmState | null>(null);
  const timers = useRef<Record<number, ReturnType<typeof setTimeout>>>({});

  const removeToast = useCallback((id: number) => {
    setToasts(prev => prev.filter(x => x.id !== id));
    clearTimeout(timers.current[id]);
    delete timers.current[id];
  }, []);

  const showToast = useCallback((message: string, type: ToastType = 'info') => {
    const id = ++toastId;
    setToasts(prev => [...prev, { id, message, type }]);
    timers.current[id] = setTimeout(() => removeToast(id), 4000);
  }, [removeToast]);

  const confirm = useCallback((message: string, opts?: ConfirmOpts) => {
    return new Promise<boolean>(resolve => {
      setConfirmState({ message, resolve, ...opts });
    });
  }, []);

  function resolveConfirm(result: boolean) {
    confirmState?.resolve(result);
    setConfirmState(null);
  }

  return (
    <ToastContext.Provider value={{ showToast }}>
      <ConfirmContext.Provider value={{ confirm }}>
        {children}

        {/* Toasts */}
        <div style={{
          position: 'fixed', bottom: 44, right: 20, zIndex: 10000,
          display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 360,
        }}>
          {toasts.map(item => (
            <div
              key={item.id}
              onClick={() => removeToast(item.id)}
              style={{
                padding: '12px 16px', borderRadius: 8, cursor: 'pointer',
                fontSize: 14, color: 'white', boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
                background: item.type === 'success' ? '#16a34a' : item.type === 'error' ? '#dc2626' : '#334155',
              }}
            >
              {item.message}
            </div>
          ))}
        </div>

        {/* Confirm dialog */}
        {confirmState && (
          <div
            onClick={() => resolveConfirm(false)}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', zIndex: 10001,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <div
              onClick={e => e.stopPropagation()}
              style={{
                background: 'white', borderRadius: 10, padding: 20, maxWidth: 380, width: '90%',
                boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
              }}
            >
              <p style={{ fontSize: 14, color: '#1e293b', marginBottom: 18, lineHeight: 1.5 }}>
                {confirmState.message}
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button
                  onClick={() => resolveConfirm(false)}
                  style={{ padding: '7px 14px', background: '#f1f5f9', color: '#334155', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14 }}
                >
                  {confirmState.cancelLabel ?? t('common.cancel')}
                </button>
                <button
                  onClick={() => resolveConfirm(true)}
                  className={confirmState.danger ? undefined : 'btn btn-primary'}
                  style={confirmState.danger
                    ? { padding: '7px 14px', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14, background: '#dc2626' }
                    : { fontSize: 14 }}
                >
                  {confirmState.confirmLabel ?? t('common.confirm')}
                </button>
              </div>
            </div>
          </div>
        )}
      </ConfirmContext.Provider>
    </ToastContext.Provider>
  );
}
